import React, { useEffect, useState } from 'react';
import { X, AlertCircle, AlertTriangle } from 'lucide-react';
import { stcBinService } from '../../../../../services/stcBinService';
import { dataInspectionService } from '../../../../../services/dataInspectionService';
import { reasonCodeService, ReasonCodeDto } from '../../../../../services/reasonCodeService';
import { ScanTextInput, NumericInput } from '../../../../common/inputs';
import { useTranslation } from '../../../../../contexts/I18nProvider';

export interface ShortageResolutionPayload {
  newBinCode: string;
  discoveredQuantity: number;
  reasonCodeNo: number;
  comment?: string;
}

interface LocationOverrideModalProps {
  isOpen: boolean;
  expectedLocation: string;
  overrideLocation: string;
  companyNo: string;
  warehouseID: string;
  itemNo: string;
  requiredQuantity: number;
  /**
   * True når plukklinjens BinCode er '[X]' (shortage). Sammen med isAdvancedWarehouse
   * styrer dette om "Jeg ser N stk her"-panelet vises ved saldo-validation-feil.
   */
  isShortageLine?: boolean;
  /**
   * True for AW-lager (Warehouse.UseAWYesNo=1). Shortage-resolution er kun
   * tilgjengelig her — i Standard-modus (Compass GO) dikterer ERP lokasjon.
   */
  isAdvancedWarehouse?: boolean;
  /**
   * False = vare-GO (Items.StcTransactionsYesNo=false): varen er ikke saldo-styrt og har
   * ingen ItemBins-rader. Da hopper vi over bin-eksistens- og saldo-validering — ERP er
   * master på lokasjon/saldo. Default true (lagerstyrt, eksisterende oppførsel).
   */
  isStockControlled?: boolean;
  onLocationChange: (value: string) => void;
  /** Kalles når operatør har valgt en gyldig bin med tilstrekkelig saldo (vanlig flyt). */
  onConfirm: () => void;
  /** Kalles når operatør har bekreftet shortage-resolution med visuell telling. */
  onResolveShortage?: (payload: ShortageResolutionPayload) => Promise<void>;
  onClose: () => void;
}

type ModalPhase = 'override' | 'shortage';

export const LocationOverrideModal: React.FC<LocationOverrideModalProps> = ({
  isOpen,
  expectedLocation,
  overrideLocation,
  companyNo,
  warehouseID,
  itemNo,
  requiredQuantity,
  isShortageLine = false,
  isAdvancedWarehouse = false,
  isStockControlled = true,
  onLocationChange,
  onConfirm,
  onResolveShortage,
  onClose
}) => {
  const { t } = useTranslation();
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [phase, setPhase] = useState<ModalPhase>('override');
  const [currentBinQuantity, setCurrentBinQuantity] = useState<number>(0);
  const [discoveredQuantity, setDiscoveredQuantity] = useState<string>('');
  const [reasonCodes, setReasonCodes] = useState<ReasonCodeDto[]>([]);
  const [selectedReasonCodeNo, setSelectedReasonCodeNo] = useState<number | ''>('');
  const [comment, setComment] = useState<string>('');
  const [isLoadingReasonCodes, setIsLoadingReasonCodes] = useState(false);
  const [isSubmittingShortage, setIsSubmittingShortage] = useState(false);
  // C3 (Ariens): hvor varen ellers har saldo — vises som klikkbare forslag i override-fasen.
  const [itemLocations, setItemLocations] = useState<{ binCode: string; available: number }[]>([]);

  const shortageEnabled = isShortageLine && isAdvancedWarehouse && !!onResolveShortage;

  // Reset all state når modalen åpnes
  useEffect(() => {
    if (isOpen) {
      setPhase('override');
      setValidationError(null);
      setCurrentBinQuantity(0);
      setDiscoveredQuantity('');
      setSelectedReasonCodeNo('');
      setComment('');
    }
  }, [isOpen]);

  // Last reason codes ved første åpning (kun hvis shortage-flyt er mulig)
  useEffect(() => {
    if (!isOpen || !shortageEnabled || reasonCodes.length > 0) return;
    let cancelled = false;
    setIsLoadingReasonCodes(true);
    reasonCodeService.getCurrent()
      .then(codes => {
        if (cancelled) return;
        // Vis kun "Inn på lager"-koder (ReasonType=2) og generiske (null/0) — vi korrigerer
        // saldo INN på en lokasjon, så "Ut av lager"-koder (ReasonType=1) er ikke relevante.
        const filtered = codes.filter(c => !c.reasonType || c.reasonType === 2);
        setReasonCodes(filtered);
      })
      .catch(err => {
        console.error('Failed to load reason codes:', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingReasonCodes(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, shortageEnabled, reasonCodes.length]);

  // C3 (Ariens): hent alle lokasjoner der varen har saldo, så operatøren ser hvor den finnes.
  // Kun AW (UseAWYesNo=1): i Compass GO er ItemBins et ERP-speil som kan drifte, så vi viser
  // ikke potensielt utdaterte saldo-lokasjoner der.
  useEffect(() => {
    if (!isOpen || !isStockControlled || !isAdvancedWarehouse || !itemNo) {
      setItemLocations([]);
      return;
    }
    let cancelled = false;
    dataInspectionService.getItemBinsByItemNo(itemNo)
      .then((bins) => {
        if (cancelled) return;
        const locs = (bins as Array<{ binCode?: string; quantityAvailable?: number; quantity?: number }>)
          .map((b) => ({ binCode: b.binCode ?? '', available: b.quantityAvailable ?? b.quantity ?? 0 }))
          .filter((l) => l.binCode && l.binCode !== '[X]' && l.available > 0)
          .sort((a, b) => b.available - a.available);
        setItemLocations(locs);
      })
      .catch((err) => console.error('Failed to load item locations:', err));
    return () => { cancelled = true; };
  }, [isOpen, isStockControlled, itemNo]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!overrideLocation.trim()) {
      setValidationError(t('warehouse.modules.picking.locationOverride.errorLocationEmpty'));
      return;
    }

    // Vare-GO (isStockControlled=false): ingen ItemBins-rader, ERP er master på lokasjon/saldo.
    // Hopp over bin-eksistens- og saldo-validering og fortsett rett til override-flyten.
    if (!isStockControlled) {
      onConfirm();
      return;
    }

    setIsValidating(true);
    setValidationError(null);

    try {
      // Valider at lokasjonen finnes i StcBin
      const locationExists = await stcBinService.exists(
        companyNo,
        warehouseID,
        overrideLocation.trim()
      );

      if (!locationExists) {
        setValidationError(t('warehouse.modules.picking.locationOverride.errorLocationNotFound', { 0: overrideLocation }));
        setIsValidating(false);
        return;
      }

      // Valider at varen har tilstrekkelig saldo på lokasjonen
      const itemBins = await dataInspectionService.getItemBinsByBinCode(overrideLocation.trim());
      const itemOnLocation = itemBins.find(ib => ib.itemNo.toLowerCase() === itemNo.toLowerCase());
      const availableQty = itemOnLocation?.quantityAvailable || 0;

      if (availableQty < requiredQuantity) {
        // Saldo utilstrekkelig — for AW-shortage-linjer åpner vi "se fysisk"-panelet
        // i stedet for å bare stoppe operatøren.
        if (shortageEnabled) {
          setCurrentBinQuantity(availableQty);
          // Foreslå required som default — operatør kan øke hvis de ser mer
          setDiscoveredQuantity(String(requiredQuantity));
          setPhase('shortage');
          setIsValidating(false);
          return;
        }

        setValidationError(
          t('warehouse.modules.picking.locationOverride.errorInsufficientStock', { 0: itemNo, 1: availableQty, 2: overrideLocation, 3: requiredQuantity })
        );
        setIsValidating(false);
        return;
      }

      // Lokasjon og saldo OK — fortsett med vanlig override-flyt
      onConfirm();
    } catch (error) {
      console.error('Error validating location:', error);
      setValidationError(t('warehouse.modules.picking.locationOverride.errorValidationFailed'));
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmitShortage = async () => {
    if (!onResolveShortage) return;

    const discoveredNum = parseFloat(discoveredQuantity);
    if (!Number.isFinite(discoveredNum) || discoveredNum <= 0) {
      setValidationError(t('warehouse.modules.picking.locationOverride.errorQuantityInvalid'));
      return;
    }
    if (discoveredNum < requiredQuantity) {
      setValidationError(
        t('warehouse.modules.picking.locationOverride.errorQuantityBelowRequired', { 0: requiredQuantity, 1: discoveredNum })
      );
      return;
    }
    if (!selectedReasonCodeNo) {
      setValidationError(t('warehouse.modules.picking.locationOverride.errorSelectReasonCode'));
      return;
    }

    setIsSubmittingShortage(true);
    setValidationError(null);
    try {
      await onResolveShortage({
        newBinCode: overrideLocation.trim(),
        discoveredQuantity: discoveredNum,
        reasonCodeNo: selectedReasonCodeNo as number,
        comment: comment.trim() || undefined
      });
      // Parent lukker modalen og refresher state
    } catch (error) {
      console.error('Error resolving shortage:', error);
      setValidationError(
        error instanceof Error ? error.message : t('warehouse.modules.picking.locationOverride.errorShortageResolutionFailed')
      );
    } finally {
      setIsSubmittingShortage(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isValidating && phase === 'override') {
      handleConfirm();
    }
  };

  const handleLocationChange = (value: string) => {
    setValidationError(null);
    onLocationChange(value);
  };

  const handleBackToOverride = () => {
    setPhase('override');
    setValidationError(null);
  };

  const correctionDelta = Math.max(0, (parseFloat(discoveredQuantity) || 0) - currentBinQuantity);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg p-4 sm:p-6 sm:max-w-md overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {phase === 'override' ? t('warehouse.modules.picking.locationOverride.title') : t('warehouse.modules.picking.locationOverride.confirmVisualCountTitle')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
            disabled={isValidating || isSubmittingShortage}
          >
            <X size={24} />
          </button>
        </div>

        {phase === 'override' && (
          <>
            <p className="text-sm text-gray-600 mb-4">
              {t('warehouse.modules.picking.locationOverride.originalLocation')}: <span className="font-medium">{expectedLocation}</span>
            </p>

            <ScanTextInput
              placeholder={t('warehouse.modules.picking.locationOverride.newLocationPlaceholder')}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 mb-2 min-h-[44px] ${
                validationError ? 'border-red-500' : 'border-gray-300'
              }`}
              value={overrideLocation}
              onChange={(e) => handleLocationChange(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isValidating}
              autoFocus
            />

            {validationError && (
              <div className="flex items-start gap-2 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-600">{validationError}</p>
              </div>
            )}

            {itemLocations.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-1">
                  {t('warehouse.modules.picking.locationOverride.itemFoundAt', {}, 'Varen finnes på:')}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {itemLocations.map((loc) => (
                    <button
                      key={loc.binCode}
                      type="button"
                      onClick={() => handleLocationChange(loc.binCode)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-blue-200 bg-blue-50 text-blue-700 text-xs hover:bg-blue-100"
                    >
                      <span className="font-mono font-medium">{loc.binCode}</span>
                      <span className="text-blue-500">({loc.available})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleConfirm}
                disabled={isValidating || !overrideLocation.trim()}
                className="flex-1 bg-blue-600 text-white py-3 sm:py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed min-h-[44px] order-1"
              >
                {isValidating ? t('warehouse.modules.picking.locationOverride.validating') : t('common.buttons.confirm')}
              </button>
              <button
                onClick={onClose}
                disabled={isValidating}
                className="px-4 py-3 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed min-h-[44px] order-2"
              >
                {t('common.buttons.cancel')}
              </button>
            </div>
          </>
        )}

        {phase === 'shortage' && (
          <>
            <div className="flex items-start gap-2 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-900">
                <p className="font-medium mb-1">{t('warehouse.modules.picking.locationOverride.insufficientStockTitle')}</p>
                <p>
                  {t('warehouse.modules.picking.locationOverride.bin')} <span className="font-mono font-medium">{overrideLocation}</span> {t('warehouse.modules.picking.locationOverride.has')}{' '}
                  <span className="font-medium">{currentBinQuantity}</span> {t('warehouse.general.unitDefault')} {t('warehouse.modules.picking.locationOverride.of')}{' '}
                  <span className="font-mono">{itemNo}</span> {t('warehouse.modules.picking.locationOverride.registeredBut')}{' '}
                  <span className="font-medium">{requiredQuantity}</span> {t('warehouse.modules.picking.locationOverride.isNeeded')}.
                </p>
              </div>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.modules.picking.locationOverride.howManySeenLabel')}
            </label>
            <NumericInput
              mode="integer"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-3 min-h-[44px]"
              value={discoveredQuantity}
              onChange={(e) => {
                setValidationError(null);
                setDiscoveredQuantity(e.target.value);
              }}
              disabled={isSubmittingShortage}
              autoFocus
            />

            {correctionDelta > 0 && (
              <p className="text-xs text-gray-500 mb-3">
                {t('warehouse.modules.picking.locationOverride.correctionPrefix')} <span className="font-medium">+{correctionDelta}</span> {t('warehouse.general.unitDefault')} {t('warehouse.modules.picking.locationOverride.correctionOn')}{' '}
                {overrideLocation} {t('warehouse.modules.picking.locationOverride.correctionInternal')}
              </p>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.modules.picking.locationOverride.reasonCode')} <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-3 min-h-[44px] bg-white"
              value={selectedReasonCodeNo}
              onChange={(e) => {
                setValidationError(null);
                setSelectedReasonCodeNo(e.target.value ? Number(e.target.value) : '');
              }}
              disabled={isSubmittingShortage || isLoadingReasonCodes}
            >
              <option value="">
                {isLoadingReasonCodes ? t('warehouse.modules.picking.locationOverride.loading') : t('warehouse.modules.picking.locationOverride.selectReason')}
              </option>
              {reasonCodes.map(rc => (
                <option key={rc.reasonCodeNo} value={rc.reasonCodeNo}>
                  {rc.reasonDescription || t('warehouse.modules.picking.locationOverride.codeLabel', { 0: rc.reasonCodeNo })}
                </option>
              ))}
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.modules.picking.locationOverride.comment')} <span className="text-gray-400 font-normal">{t('warehouse.modules.picking.locationOverride.optional')}</span>
            </label>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-3 min-h-[60px] resize-none"
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={isSubmittingShortage}
              placeholder={t('warehouse.modules.picking.locationOverride.commentPlaceholder')}
            />

            {validationError && (
              <div className="flex items-start gap-2 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-600">{validationError}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSubmitShortage}
                disabled={isSubmittingShortage || !discoveredQuantity || !selectedReasonCodeNo}
                className="flex-1 bg-blue-600 text-white py-3 sm:py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed min-h-[44px] order-1"
              >
                {isSubmittingShortage ? t('warehouse.modules.picking.locationOverride.confirming') : t('warehouse.modules.picking.locationOverride.confirmAndCorrect')}
              </button>
              <button
                onClick={handleBackToOverride}
                disabled={isSubmittingShortage}
                className="px-4 py-3 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed min-h-[44px] order-2"
              >
                {t('common.buttons.back')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
